import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";

import { Close as CloseIcon } from "@mui/icons-material";
import { Box, ListItemButton, IconButton, Snackbar } from "@mui/material";

import withErrorHandling from "../../HOC/ErrorHandling";
import { useSharedProps } from "../../HOC/SharedProps";
import WelcomeNamingModal from "../../components/WelcomeNamingModal";
import GettingStartedModal from "../../components/GettingStartedModal";
import { getEnv } from "../../helpers/env";
import { getToken } from "../../helpers/tokenStorage";

import usePopup from "../../helpers/contexts/usePopup";
import {
  getProjects,
  getUsers,
  newProject,
  deleteProject,
  updateProject,
  updateProjectLockedStatus,
  getNotifications,
  setNotifications2Read,
} from "../../helpers/utils";
import { filterRecords, changeTxt } from "../../helpers/selectors";
import t from "../../helpers/en";

import AppBar from "../../components/AppBarMain";
import DrawerMain from "../../components/DrawerMain";
import MainListItems from "./NavItems";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Footer from "../../components/Footer/Footer";
import PopupWindow from "../../components/PopupWindow";
import Modal from "../../components/Modal/Modal";
import ManageProjects from "./ManageProjects";
import Notifications from "../../components/Notifications";
// SCK-549 PR-7 — floating support chat widget. Mounts on every
// authenticated console page (Main is the authenticated shell). Renders
// null when REACT_APP_SUPPORT_WIDGET_ENABLED is off, so the mount is a
// no-op in envs that haven't flipped the flag yet.
import { SupportWidget } from "../../features/support";

const Main = (props) => {
  const { onLogout, currentUser, navItems, showError, handleThemeToggle, isDarkMode } = props;

  const [open, setOpen] = useState(true);

  // SCK-638 — accordion state persists per-user across sessions.
  // Read once from localStorage; every toggle writes back. Only ids
  // that appear in the current NAV_ITEMS matter, but we keep any
  // extras around so a stale key doesn't get dropped on write.
  const NAV_STATE_KEY = "c2a.nav.expanded";
  const readNavState = () => {
    try {
      const raw = window.localStorage.getItem(NAV_STATE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const [toggledMenuList, setToggledMenuList] = useState(() => {
    const persisted = readNavState();
    // Preserve legacy defaults on first mount so the Apps section
    // starts open for existing users who have no stored preference.
    return persisted || { list1: true, list2: false };
  });
  const [data, setData] = useState({ all: [], filtered: [] });
  const [viewport, setViewport] = useState(window.innerWidth);
  useEffect(() => {
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setViewport(window.innerWidth));
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  const [modalData, setModalData] = useState({ open: false, tabValue: 0 });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [state, setState] = useState({
    data: {},
    ws: null,
    currentSnackbarMsg: "",
    anchorEl: false,
    page: 0,
  });

  const { onSetGroup, currentGroup } = useSharedProps();
  const {
    buttonRef,
    popupRef,
    popupPosition,
    openPopup,
    closePopup,
    onMouseLeave,
    openPopupForItem,
  } = usePopup(80);
  const location = useLocation();

  // 960 = MUI's md breakpoint. Below this, drawer becomes a modal
  // (temporary) with backdrop; above, it's a persistent shelf that
  // shares layout width with main content. 1200 was too aggressive —
  // most laptops at moderate zoom drop under 1200 and got the
  // modal-drawer overlay on every nav interaction.
  const isSmallScreen = viewport < 960;

  const fetchData = (params, cb) => {
    getProjects((result, errorMsg) => {
      if (result) {
        const updated = result.map((group) => {
          // Additive-privilege rule: SYSTEM users have global access, and
          // ACCOUNT_ADMINs have access to every project inside their own
          // account, without needing an explicit userRights row. Both cases
          // must short-circuit isContributor to true, else every gated
          // action (Add App, Rebuild, Delete, etc.) silently disables.
          // See memory: feedback_system_additive_privilege.
          const isSystem = params.currentUser?.userType === "SYSTEM";
          const isAccountAdminOfThisProject =
            group?.accountId &&
            params.currentUser?.account?.id === group.accountId &&
            (params.currentUser?.groups || []).some(
              (g) => g?.userRole?.roleType === "ACCOUNT_ADMIN"
            );
          const isContributor =
            isSystem ||
            isAccountAdminOfThisProject ||
            group?.userRights?.find(
              (user) => user?.user?.id === params.currentUser?.uid
            )?.isContributor;

          return {
            ...group,
            isContributor2Group: isContributor,
          };
        });

        params.setData({ all: updated, filtered: updated });

        if (cb) cb(updated);
      } else {
        params.showError(errorMsg);
      }
    });
  };

  useEffect(() => {
    fetchData({ setData, showError, currentUser }, (result) => {
      if (
        !sessionStorage.getItem("projectGroupSetOnLogin") ||
        (currentGroup?.id && !result?.find((it) => it.id === currentGroup?.id))
      ) {
        const defaultProject =
          Array.isArray(result) &&
          (result.find(({ userRights }) =>
            userRights.some(
              ({ isUserDefault, user }) =>
                isUserDefault && user.id === currentUser.uid
            )
          ) ||
            result?.[0]);

        sessionStorage.setItem("projectGroupSetOnLogin", true);

        if (defaultProject) {
          onSetGroup(defaultProject);
        }
      } else {
        const selectedGroup = result?.find((it) => it.id === currentGroup?.id);

        // Check if the currentGroup exists and if there are any differences between currentGroup and selectedGroup
        if (
          selectedGroup &&
          JSON.stringify(currentGroup) !== JSON.stringify(selectedGroup)
        ) {
          onSetGroup(selectedGroup);
        }
      }
    });
  }, [showError, setData, currentUser, currentGroup, onSetGroup]);

  useEffect(() => {
    const onResize = () => {
      setViewport(window.innerWidth);
      closePopup();
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [closePopup]);

  // NOTIFICATIONS START
  useEffect(() => {
    setState((prev) => ({ ...prev, loading: true }));
    getNotifications({ pageNumber: 0, pageSize: 5 }, (result, err) => {
      if (!err) {
        setState((prev) => ({
          ...prev,
          data: result,
          loading: false,
        }));
      } else {
        showError(err);
        setState((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    });
  }, [showError, currentUser]);

  useEffect(() => {
    const token = getToken();
    const socket = new WebSocket(
      `${getEnv("REACT_APP_WS_URL")}?token=${encodeURI(token)}`
    );

    socket.onopen = () => console.warn("WebSocket connected");
    socket.onclose = () => console.warn("WebSocket disconnected");

    socket.onmessage = (evt) => {
      const message = evt.data && JSON.parse(evt.data);

      setState((prev) => ({
        ...prev,
        data: { ...prev.data, content: [message, ...prev.data.content] },
        currentSnackbarMsg: message?.title,
      }));
    };

    setState((prev) => ({
      ...prev,
      ws: socket,
    }));

    return () => socket.close();
  }, []);

  const onSnackbarClose = () => {
    setState((prev) => ({
      ...prev,
      currentSnackbarMsg: "",
    }));
  };

  const setNotifications2ReadAsync = (unreadNotificationIds) => {
    return new Promise((resolve, reject) => {
      setNotifications2Read(unreadNotificationIds, (result, err) => {
        if (!err) {
          resolve(result);
        } else {
          reject(err);
        }
      });
    });
  };

  const markNotificationAsRead = async (props) => {
    const unreadNotificationIds =
      Array.isArray(state.data.content) &&
      state.data.content
        .filter((notification) => !notification.isRead)
        .map((notification) => notification.id);

    if (unreadNotificationIds && unreadNotificationIds.length > 0) {
      try {
        await setNotifications2ReadAsync(unreadNotificationIds);
        setState((prev) => ({
          ...prev,
          ...props,
          // Mark those notifications as read in the state immediately
          data: {
            ...prev.data,
            content: prev.data.content.map((notif) =>
              unreadNotificationIds.includes(notif.id)
                ? { ...notif, isRead: true }
                : notif
            ),
          },
        }));
      } catch (err) {
        showError(err);
      }
    } else {
      setState((prev) => ({
        ...prev,
        ...props,
      }));
    }
  };

  const onNotificationsOpen = (elem) => {
    setState((prev) => ({
      ...prev,
      anchorEl: elem,
      currentSnackbarMsg: "",
      data: { ...prev.data, content: prev.data?.content?.splice(0, 5) ?? [] },
    }));
  };

  const onNotificationsClose = () => {
    setState((prev) => ({ ...prev, anchorEl: null, page: 0 })); // Close popup immediately
    // Run markNotificationAsRead in the background
    setTimeout(() => { markNotificationAsRead({ page: 0, anchorEl: null }); }, 0);
  };

  const onLoadMoreNotifications = () => {
    setState((prev) => ({ ...prev, loading: true }));

    getNotifications(
      { pageNumber: state.page + 1, pageSize: 5 },
      (result, err) => {
        if (!err) {
          setState((prev) => ({
            ...prev,
            page: prev.page + 1,
            data: {
              ...result,
              content: [...prev.data.content, ...result.content],
            },
            loading: false,
          }));
        } else {
          showError(err);
          setState((prev) => ({
            ...prev,
            page: prev.page + 1,
            loading: false,
          }));
        }
      }
    );
  };

  // NOTIFICATIONS END

  const onToggleDrawer = () => {
    setOpen(!open);
  };

  const onToggleMenuList = (listName) => {
    setToggledMenuList((prev) => {
      const next = { ...prev, [listName]: !prev[listName] };
      try {
        window.localStorage.setItem(NAV_STATE_KEY, JSON.stringify(next));
      } catch {
        // localStorage may throw in private-mode / storage-full;
        // the accordion still works in-memory this session.
      }
      return next;
    });
  };

  // Auto-expand the section whose route the viewer just entered — a
  // one-click-from-anywhere guarantee (SCK-638). Sibling sections
  // stay in whatever expand/collapse state the user chose; we never
  // collapse an open section here.
  useEffect(() => {
    if (!Array.isArray(navItems) || navItems.length === 0) return;
    const path = location.pathname;
    const match = navItems.find(
      (item) =>
        item?.id &&
        item?.subItems?.length &&
        // Accept exact match OR nested path under the section root.
        (path === item.to ||
          (item.to !== "/" && path.startsWith(item.to + "/")) ||
          item.subItems.some(
            (sub) => sub.to && (path === sub.to || path.startsWith(sub.to + "/"))
          ))
    );
    if (!match?.id) return;
    setToggledMenuList((prev) => {
      if (prev[match.id]) return prev; // already open, leave alone
      const next = { ...prev, [match.id]: true };
      try {
        window.localStorage.setItem(NAV_STATE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, [location.pathname, navItems]);

  const onFilterRecords = (result, currentFilters) => {
    const res = filterRecords(result, currentFilters, (item) => [item.name]);

    setData(res);
  };

  const onSaveSuccess = (callback) => {
    fetchData({ setData, showError, currentUser }, (result) => {
      callback && callback(result);
      onClose();
    });
  };

  const onOpen = (evt) => {
    setModalData({
      open: true,
      tabValue: 0,
      options: [],
      selectedUsers: [],
    });
  };

  const onClose = () => {
    setModalData({ open: false });
  };

  const onChangeTab = (_, val) => {
    setModalData((prev) => ({
      ...prev,
      tabValue: val,
      id: null,
      name: null,
      selectedUsers: modalData?.id
        ? []
        : [
            {
              ...({ ...currentUser, id: currentUser?.uid } ?? {}),
              isContributor: true,
            },
          ],
    }));
  };

  const onChangeSearchTxt = (evt) => {
    const { value } = evt.target;

    setModalData((prev) => ({ ...prev, searchedValue: value }));
  };

  const onClearSearchTxt = () => {
    const copyFilters = { ...modalData, searchedValue: null };

    setModalData(copyFilters);
    onFilterRecords(data?.all, copyFilters);
  };

  const onKeyPressSearch = (evt) => {
    evt.preventDefault();

    if (evt.key === "Enter") {
      onFilterRecords(data?.all, modalData);
    }
  };

  const onChangeSearchTxtUser = (evt) => {
    const { value } = evt.target;

    setModalData((prev) => ({ ...prev, searchedUser: value }));
  };

  const onClearSearchTxtUser = () => {
    const copyFilters = { ...modalData, searchedUser: null };

    setModalData(copyFilters);
  };

  const onKeyPressSearchUser = (evt) => {
    evt.preventDefault();

    if (evt.key === "Enter") {
      if (modalData?.searchedUser) {
        setModalData((prev) => ({
          ...prev,
          isSearchingUser: true,
        }));
        getUsers(
          (result, err) => {
            if (!err) {
              setModalData((prev) => ({
                ...prev,
                options: result
                  ? [result, ...modalData.options]
                  : modalData?.options,
                selectedUsers: [
                  ...prev.selectedUsers,
                  ...(result?.id &&
                  !prev.selectedUsers.some((it) => it.id === result?.id)
                    ? [result]
                    : []),
                ],
                searchedUser: null,
                isSearchingUser: false,
              }));
            } else {
              showError(err);
              setModalData((prev) => ({
                ...prev,
                isSearchingUser: false,
              }));
            }
          },
          { email: modalData?.searchedUser, shouldSearchByEmail: true }
        );
      }
    }
  };

  const onChangeTxt = (evt) => {
    const result = changeTxt(evt, modalData);

    // SCK-721: mark the name field as "touched" the first time the
    // user interacts with it so the required-field error only renders
    // after an edit (not on initial render of the Create tab).
    if (evt?.target?.name === "name") {
      result.touchedName = true;
    }

    setModalData(result);
  };

  const onChangeCheckbox = (evt, item, idx) => {
    setModalData((prev) => {
      const { isContributor } = item;

      // Update the selectedUsers array, toggling the isContributor value for the item at idx
      const updatedSelectedUsers = prev.selectedUsers.map((user, index) =>
        index === idx ? { ...user, isContributor: !isContributor } : user
      );

      return { ...prev, selectedUsers: updatedSelectedUsers };
    });
  };

  const onToggleLockedStatus = (item) => {
    const updatedStatus = !item.locked;

    updateProjectLockedStatus(
      { id: item.id, locked: updatedStatus },
      (result, err) => {
        if (!err) {
          fetchData({ setData, showError, currentUser });
        } else {
          showError(err);
        }
      }
    );
  };

  const onError = (err) => {
    showError(err);
    setModalData((prev) => ({ ...prev, isSubmitting: false }));
  };

  const setProject = () => {
    if (modalData?.isSubmitting) return;

    // SCK-721: belt-and-braces guard — the Save button is disabled on
    // invalid name via Modal's saveDisabled prop, but hard-stop here
    // too so a programmatic trigger can't submit a blank project.
    if (modalData?.name == null || modalData.name.trim() === "") {
      setModalData((prev) => ({ ...prev, touchedName: true }));
      return;
    }

    const onSave = (operation, callback) => {
      setModalData((prev) => ({ ...prev, isSubmitting: true }));
      operation(modalData, (result, err) => {
        if (err) {
          onError(err);
        } else {
          onSaveSuccess(callback);
        }
      });
    };

    if (!modalData?.id) {
      onSave(newProject); // Create a new project
    } else {
      onSave(updateProject, (result) => {
        const selectedGroup = result?.find((it) => it.id === currentGroup?.id);

        onSetGroup(selectedGroup);
      }); // Update an existing project
    }
  };

  const onEditGroup = (_, el) => {
    setModalData((prev) => ({
      ...prev,
      tabValue: 1,
      ...el,
      selectedUsers: el.userRights?.map((it) => ({
        ...it.user,
        isContributor: it.isContributor,
      })),
    }));
  };

  const onDeleteGroup = (el) => {
    setProjectToDelete(el);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    if (modalData?.isSubmitting) return;

    setModalData((prev) => ({
      ...prev,
      isSubmitting: true,
      currentSelectedItem: projectToDelete,
    }));

    deleteProject(projectToDelete, (result, err) => {
      if (!err) {
        onSaveSuccess();
        if (projectToDelete?.id === currentGroup?.id) {
          onSetGroup(projectToDelete);
        }
      } else {
        onError(err);
      }

      handleCancelDelete();
    });
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setProjectToDelete(null);
  };

  const onRemoveUser = (idx) => {
    const cpy = { ...modalData };

    cpy?.selectedUsers?.splice(idx, 1);
    setModalData(cpy);
  };

  return (
    <div className="app" style={{ background: isDarkMode ? "#0f172a" : "#f1f5f9", minHeight: "100dvh", transition: "background 0.3s ease" }}>
      <AppBar
        {...{
          open,
          onLogout,
          currentUser,
          onToggleDrawer,
          viewport,
          modalData,
          onOpen,
          projectToDelete,
          handleConfirmDelete,
          handleCancelDelete,
          confirmDelete,
          handleThemeToggle,
          isDarkMode,
        }}
        notifications={state?.data?.content ?? []}
        onMenuOpen={onNotificationsOpen}
      />
      {/* Notifications */}
      <Notifications
        {...{ state, onLoadMoreNotifications }}
        onClose={onNotificationsClose}
      />

      {/* New Notification */}
      {state.currentSnackbarMsg && (
        <Snackbar
          open={!!state.currentSnackbarMsg}
          message={state.currentSnackbarMsg}
          action={
            <IconButton size="small" onClick={onSnackbarClose}>
              <CloseIcon />
            </IconButton>
          }
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiSnackbarContent-root": {
              background: "#fff",
              fontSize: "0.9rem",
              color: "#333",
            },
            marginTop: "2rem",
          }}
        />
      )}

      <Box style={{ display: "flex", height: "calc(100% - 3.5em)", background: isDarkMode ? "#0f172a" : "#f1f5f9", transition: "background 0.3s ease" }}>
        <DrawerMain
          {...{ open, onToggleDrawer, viewport, isDarkMode }}
          menuList={
            <MainListItems
              {...{
                navItems,
                viewport,
                open,
                onToggleMenuList,
                toggledMenuList,
                openPopup,
                buttonRef,
                isDarkMode,
              }}
            />
          }
        />

        {!isSmallScreen && openPopupForItem && (
          <div ref={popupRef} onMouseEnter={() => {}} {...{ onMouseLeave }}>
            <PopupWindow
              specificDetails={{
                onClose: closePopup,
                popupPosition: popupPosition,
                style: { div: { width: "160px" } },
                actions: openPopupForItem.subItems?.map((subItem, itemIdx) => (
                  <ListItemButton
                    key={itemIdx}
                    component={NavLink}
                    sx={{
                      fontSize: "13px",
                      ...(location.pathname === subItem.to
                        ? {
                            background: (theme) =>
                              theme.palette.primary.activeLink,
                            color: (theme) => theme.palette.primary.text,
                          }
                        : {}),
                    }}
                    onClick={closePopup}
                    to={subItem.to}
                    role="menuitem"
                    aria-label={`Navigate to ${subItem.label}`}
                    disableRipple
                  >
                    {subItem.label}
                  </ListItemButton>
                )),
              }}
            />
          </div>
        )}

        <main
          id="main"
          style={{
            maxWidth: "2500px",
            margin: "1rem",
            background: isDarkMode ? "#1e293b" : "#ffffff",
            borderRadius: "12px",
            padding: "1rem",
            ...(!isSmallScreen
              ? { marginLeft: open ? 316 : 116 }
              : { marginLeft: "1rem" }),
            transition: "margin-left 0.3s, background 0.3s ease",
            width: !isSmallScreen
              ? open
                ? "calc(100% - 316px - 1rem)"
                : "calc(100% - 116px - 1rem)"
              : "calc(100% - 2rem)",
            height: "calc(100% - 2em)",
          }}
        >
          <Breadcrumbs />

          <Outlet />

          <Footer />
        </main>
      </Box>

      {/* First-login workspace naming prompt — fires once when the
          user has any project flagged needsNaming=true (the auto-
          created project from invitation redemption). Self-detects
          on mount; renders nothing otherwise. */}
      <WelcomeNamingModal />

      {/* SCK-607 — cross-cutting first-time getting-started modal.
          Fires ONCE per user after NDA acceptance + first sign-in;
          content varies by invitation kind (SOLO / ACCOUNT_MEMBER /
          PROJECT_MEMBER / BETA / ACCOUNT_ADMIN / null-generic).
          Self-detects (localStorage → GET /api/me/preferences → GET
          /api/me/invitation-kind); renders nothing when dismissed. */}
      <GettingStartedModal />

      {/* SCK-549 PR-7 — floating support chat. Feature-flagged; the
          component self-hides when REACT_APP_SUPPORT_WIDGET_ENABLED
          is off. It's mounted here (not in App.js) so it only appears
          for signed-in viewers — the auth shell IS Main. */}
      <SupportWidget />

      {/* MODAL */}

      <Modal
        open={Boolean(modalData?.open)}
        handleClose={onClose}
        title={t.manageProjects}
        saveLabel={modalData?.tabValue === 1 && t.save}
        onSave={modalData?.tabValue === 1 && setProject}
        specificDetails={{
          isSubmitting: modalData?.isSubmitting,
          // SCK-721: disable Save on the Create / Edit project tab when
          // the name field is empty. Backend also rejects blank names
          // (K8sProjectController.createProject + K8sProjectService
          // updateProject/onboardName) and V79 adds the DB-level CHECK.
          saveDisabled:
            modalData?.tabValue === 1 &&
            (modalData?.name == null || String(modalData.name).trim() === ""),
        }}
        closeLabel={t.close}
        content={
          <ManageProjects
            {...{
              data,
              onChangeTab,
              onChangeSearchTxt,
              onClearSearchTxt,
              onKeyPressSearch,
              onRemoveUser,
              onKeyPressSearchUser,
              onChangeCheckbox,
              onClearSearchTxtUser,
              onChangeSearchTxtUser,
              onChangeTxt,
              onDeleteGroup,
              onEditGroup,
              onSetGroup,
              modalData,
              currentGroup,
              currentUser,
              onToggleLockedStatus,
            }}
          />
        }
      />
    </div>
  );
};

export default withErrorHandling(Main);
